/** @module store/ems_current_status */
import * as apiEmsCurrentStatus from '@/api/ems_current_status'
import { subscribe } from '@/api/action_cable'

export const SYSTEM_CONFIGURATION_STATUS_FLAG = {
  unknown: 1,
  std: 2,
  nonStd: 4
}

const state = () => ({
  emsToEmsCurrentStatus: {}
})

const mutations = {
  SET_EMS_CURRENT_STATUS: (state, { emsCurrentStatus }) => {
    state.emsToEmsCurrentStatus = emsCurrentStatus
      .filter((emsCS) => emsCS.energyManagementSystemId && emsCS instanceof EmsCurrentStatus)
      .reduce((prev, emsCS) => ({ ...prev, [emsCS.energyManagementSystemId]: emsCS }), {})
  },
  UPDATE_EMS_CURRENT_STATUS: (state, { emsCurrentStatus }) => {
    if (!emsCurrentStatus.energyManagementSystemId || !(emsCurrentStatus instanceof EmsCurrentStatus)) return

    state.emsToEmsCurrentStatus[emsCurrentStatus.energyManagementSystemId] = emsCurrentStatus
  },
  UPDATE_INTERNAL_EMS_CURRENT_STATUS: (state, { emsId }) => {
    if (!state.emsToEmsCurrentStatus[emsId]) return

    state.emsToEmsCurrentStatus[emsId].updateInternalStatus()
  },
  UPDATE_EMS_CURRENT_STATUS_LIVE_DATA: (state, data) => {
    if (!state.emsToEmsCurrentStatus[data.emsId]) return

    state.emsToEmsCurrentStatus[data.emsId].updateLiveData(data)
  }
}

const getters = {
  getEmsCurrentStatus: (state) => (emsId) => state.emsToEmsCurrentStatus[emsId]
}

const actions = {
  getEmsCurrentStatus: ({ commit, dispatch }, { ctxt }) =>
    apiEmsCurrentStatus.getEmsCurrentStatus({ ctxt }).then((msg) => {
      commit('SET_EMS_CURRENT_STATUS', {
        emsCurrentStatus: msg?.payload?.data?.map((emsCSResource) => resourceToEmsCurrentStatus(emsCSResource))
      })
      return dispatch('updateInternalEmsCurrentStatus')
    }),
  updateEmsCurrentStatus: ({ commit }, { ctxt, emsId, putIntoServiceAt, systemConfigurationStatus }) => {
    const attributes = _.omitBy({ putIntoServiceAt, systemConfigurationStatus }, _.isUndefined)

    return apiEmsCurrentStatus.updateEmsCurrentStatus({ ctxt, emsId, attributes }).then((msg) => {
      commit('UPDATE_EMS_CURRENT_STATUS', {
        emsCurrentStatus: resourceToEmsCurrentStatus(msg?.payload?.data)
      })
    })
  },
  updateInternalEmsCurrentStatus: ({ state, commit }) => {
    const REFRESH_INTERVAL = 10000
    const GROUP_INTERVAL = 500
    const emsIds = Object.keys(state.emsToEmsCurrentStatus)
    const emsIdsPerGroup = Math.ceil(emsIds.length / (REFRESH_INTERVAL / GROUP_INTERVAL))
    let delay = 0

    while (emsIds.length) {
      const emsGroup = emsIds.splice(0, emsIdsPerGroup)

      setTimeout(() => {
        for (const emsId of emsGroup) {
          setInterval(() => commit('UPDATE_INTERNAL_EMS_CURRENT_STATUS', { emsId }), REFRESH_INTERVAL)
        }
      }, delay)

      delay += GROUP_INTERVAL
    }
  },
  subscribeToEmsCurrentStatus: ({ commit, dispatch, rootGetters }) => {
    const params = {
      channel: 'EmsStatusChannel'
    }
    if (!rootGetters['user/isSudo']) {
      params.user_id = rootGetters['user/id']
    }
    subscribe({
      params,
      callback: (data) => {
        if (!data) return

        switch (data[0].type) {
          case 'heartbeats':
            for (const heartbeatResource of data) {
              commit('UPDATE_EMS_CURRENT_STATUS_LIVE_DATA', {
                emsId: heartbeatResource.id,
                ...heartbeatResource.attributes
              })
            }
            break
          case 'log_event_seqs':
            dispatch('logEvents/updateLogEvents', { logEventResources: data }, { root: true })
            break
        }
      },
      commit
    })
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions
}

export const STATUS_OFFLINE_AFTER = moment.duration(-5, 'minutes')
export const STATUS_DEPRECATED_AFTER = moment.duration(-30, 'days')
export class EmsCurrentStatus {
  aggregatedStatus
  latestEmsAliveAgo
  latestHeartbeatAgo

  constructor({
    activatedAt,
    energyManagementSystemId,
    latestHeartbeatTimestamp,
    latestEmsAliveTimestamp,
    latestEmsStatus,
    latestEmsSensorsTimestamp,
    maintenanceIp,
    maintenanceActive,
    nonEmptyTopology,
    putIntoServiceAt,
    systemConfigurationStatus
  }) {
    if (!energyManagementSystemId) throw new TypeError('An EMS-ID has to be present')
    this.activatedAt = activatedAt ? moment(activatedAt) : null
    this.energyManagementSystemId = parseInt(energyManagementSystemId)
    this.latestHeartbeatTimestamp = moment(latestHeartbeatTimestamp)
    this.latestEmsAliveTimestamp = moment(latestEmsAliveTimestamp)
    this.latestEmsStatus = latestEmsStatus
    this.latestEmsSensorsTimestamp = moment(latestEmsSensorsTimestamp)
    this.maintenanceIp = maintenanceIp
    this.maintenanceActive = maintenanceActive
    this.nonEmptyTopology = nonEmptyTopology || false
    this.putIntoServiceAt = putIntoServiceAt ? moment(putIntoServiceAt) : null
    this.systemConfigurationStatus = systemConfigurationStatus || SYSTEM_CONFIGURATION_STATUS_FLAG.unknown

    this.updateInternalStatus()
  }

  /**
   * Returns the aggregated CurrentStatus for an EMS
   *
   * the status can be:
   * - 'alive' => alive and online
   * - 'error' => error and online
   * - 'dead' => dead and online
   * - 'offline' => any status and offline
   * - 'deprecated' => any status and deprecated
   * - 'pending' => any status and pending
   * @function
   *
   * @return {object} status
   */
  updateInternalStatus() {
    const potentialInternalStatus = {}
    const now = moment()

    potentialInternalStatus.latestEmsAliveAgo = moment.duration(this.latestEmsAliveTimestamp - now)
    potentialInternalStatus.latestHeartbeatAgo = moment.duration(this.latestHeartbeatTimestamp - now)

    if (!this.latestHeartbeatTimestamp.isValid()) {
      potentialInternalStatus.aggregatedStatus = 'pending'
    } else if (potentialInternalStatus.latestHeartbeatAgo < STATUS_DEPRECATED_AFTER) {
      potentialInternalStatus.aggregatedStatus = 'deprecated'
    } else if (potentialInternalStatus.latestHeartbeatAgo < STATUS_OFFLINE_AFTER) {
      potentialInternalStatus.aggregatedStatus = 'offline'
    } else {
      potentialInternalStatus.aggregatedStatus = this.latestEmsStatus || 'offline'
    }

    if (this.aggregatedStatus !== potentialInternalStatus.aggregatedStatus) {
      this.aggregatedStatus = potentialInternalStatus.aggregatedStatus
    }

    const agoString = (duration) => duration?.clone()?.humanize()

    if (agoString(this.latestEmsAliveAgo) !== agoString(potentialInternalStatus.latestEmsAliveAgo))
      this.latestEmsAliveAgo = potentialInternalStatus.latestEmsAliveAgo

    if (agoString(this.latestHeartbeatAgo) !== agoString(potentialInternalStatus.latestHeartbeatAgo))
      this.latestHeartbeatAgo = potentialInternalStatus.latestHeartbeatAgo

    return potentialInternalStatus
  }

  updateLiveData(data) {
    for (const key of ['emsStatus', 'heartbeatDatetime', 'emsAliveDatetime']) {
      if (!data[key]) continue
      switch (key) {
        case 'emsStatus':
          this.latestEmsStatus = data.emsStatus
          break
        case 'heartbeatDatetime':
          this.latestHeartbeatTimestamp = moment(data.heartbeatDatetime)
          break
        case 'emsAliveDatetime':
          this.latestEmsAliveTimestamp = moment(data.emsAliveDatetime)
          break
      }
    }

    this.updateInternalStatus()
  }
}

const resourceToEmsCurrentStatus = (resource) => {
  const params = {
    energyManagementSystemId: resource?.relationships?.energyManagementSystem?.data?.id,
    ...resource.attributes
  }

  if (!params.energyManagementSystemId) return

  return new EmsCurrentStatus(params)
}
